/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from "react";
import { Checkbox, Col, Modal, Row } from "antd";
import { DeleteOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffectOnce } from "react-use";
import Button from "../../../components/Button/Button";
import { FlexCol } from "../../../components/Container/Container";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import Text from "../../../components/Text/Text";
import { getNewsShop } from "../../../datasource/News";
import { getShopGroup } from "../../../datasource/ShopGroupDatasoure";
import { zoneDatasource } from "../../../datasource/ZoneDatasource";
import { StoreEntity } from "../../../entities/StoreEntity";
import color from "../../../resource/color";
import image from "../../../resource/image";
import { ModalSelectStore } from "../../Shared/ModalSelectStore";
import { ShopTable } from "./NewsStyled";

export type NewsShop = StoreEntity & { checked?: boolean };

export interface NewsShopPayload {
  customerCompanyId: string;
  customerNo: string;
  customerName: string;
  zone: string;
  customerGroupName: string;
}

export const toNewsShopPayload = (shops: NewsShop[]): NewsShopPayload[] =>
  shops.map((shop) => ({
    customerCompanyId: `${shop.customerCompanyId ?? ""}`,
    customerNo: shop.customerNo || "",
    customerName: shop.customerName || "",
    zone: shop.zone || "",
    customerGroupName: shop.customerGroupName || "",
  }));

const unwrap = (value: any): any => {
  let result = value;
  while (typeof result === "string") {
    try {
      result = JSON.parse(result);
    } catch (e) {
      return undefined;
    }
  }
  return result;
};

export const parseNewsShopResponse = (res: any): { data: any[]; count: number } => {
  const body = unwrap(res?.responseData ?? res) || {};
  if (Array.isArray(body)) return { data: body, count: body.length };
  const list = unwrap(body?.data ?? body?.newsShops ?? body?.shops ?? []);
  const data: any[] = Array.isArray(list) ? list : [];
  const count = Number(body?.count ?? body?.total ?? data.length) || data.length;
  return { data, count };
};

export const newsShopKey = (row: any) =>
  `${row?.customerCompanyId ?? ""}-${row?.customerNo ?? ""}-${row?.customerName ?? ""}`;

export const uniqueNewsShops = <T,>(list: T[]): T[] => {
  const seen = new Set<string>();
  return list.filter((row) => {
    const key = newsShopKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const fetchAllNewsShops = async (newsId: string): Promise<NewsShop[]> => {
  const withChecked = (list: any[]) => list.map((shop) => ({ ...shop, checked: false }));

  const first = await getNewsShop({ newsId, page: 1, take: 1 });
  if (!first) throw new Error("โหลดร้านค้าที่เห็นข่าวไม่สำเร็จ");

  const { data: firstData, count } = parseNewsShopResponse(first);
  if (count <= firstData.length) return withChecked(firstData);

  const res = await getNewsShop({ newsId, page: 1, take: count });
  if (!res) throw new Error("โหลดร้านค้าที่เห็นข่าวไม่สำเร็จ");

  const { data } = parseNewsShopResponse(res);
  if (data.length < count) {
    console.log(`โหลดร้านค้าได้ ${data.length} จาก ${count} ร้าน (API จำกัด take)`);
  }

  return withChecked(data);
};

export const NewsShopEmpty = ({ text }: { text: string }) => (
  <FlexCol align='center' justify='center' style={{ width: "100%", padding: "80px 32px" }}>
    <img src={image.emptyShopTable} style={{ width: 126, height: 80 }} alt='empty_shop' />
    <br />
    <Text level={5} color='Text3'>
      {text}
    </Text>
  </FlexCol>
);

interface Props {
  company: string;
  shops: NewsShop[];
  setShops: (shops: NewsShop[]) => void;
  errorMessage?: string;
  setErrorMessage?: (message?: string) => void;
  loading?: boolean;
}

export const NewsStepTwoShop: React.FC<Props> = ({
  company,
  shops,
  setShops,
  errorMessage,
  setErrorMessage,
  loading,
}) => {
  const [zoneList, setZoneList] = useState<any[]>([]);
  const [groupList, setGroupList] = useState<any[]>([]);

  const [searchText, setSearchText] = useState<string>("");
  const [searchZone, setSearchZone] = useState<string>("");
  const [searchGroup, setSearchGroup] = useState<string>("");

  const [selectShopModal, setSelectShopModal] = useState(false);
  const [deleteShopModal, setDeleteShopModal] = useState(false);

  useEffectOnce(() => {
    fetchZone();
    fetchShopGroup();
  });

  const fetchZone = async () => {
    await zoneDatasource
      .getAllZoneByCompany(company)
      .then((res) => setZoneList(res || []))
      .catch((err) => console.log(err));
  };

  const fetchShopGroup = async () => {
    await getShopGroup({ page: 1, take: 999, company, isActive: true })
      .then((res) => setGroupList(res?.responseData?.data || []))
      .catch((err) => console.log(err));
  };

  const shopList = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    return shops.filter((shop) => {
      const matchKeyword = keyword
        ? `${shop.customerName || ""}`.toLowerCase().includes(keyword) ||
          `${shop.customerNo || ""}`.toLowerCase().includes(keyword)
        : true;
      const matchZone = searchZone ? `${shop.zone || ""}` === searchZone : true;
      const matchGroup = searchGroup ? `${shop.customerGroupName || ""}` === searchGroup : true;
      return matchKeyword && matchZone && matchGroup;
    });
  }, [shops, searchText, searchZone, searchGroup]);

  const checkedCount = shops.filter((shop) => shop.checked).length;
  const isCheckedAll = shopList.length > 0 && shopList.every((shop) => shop.checked);

  const onClearSearch = () => {
    setSearchText("");
    setSearchZone("");
    setSearchGroup("");
  };

  const onCheckShop = (customerCompanyId: string, checked: boolean) => {
    setShops(
      shops.map((shop) =>
        `${shop.customerCompanyId}` === `${customerCompanyId}` ? { ...shop, checked } : shop,
      ),
    );
  };

  const onCheckAll = (checked: boolean) => {
    /* ติ๊กทั้งหมดเฉพาะร้านที่อยู่ในผลการค้นหาปัจจุบัน */
    const inSearch = shopList.map((shop) => `${shop.customerCompanyId}`);
    setShops(
      shops.map((shop) =>
        inSearch.includes(`${shop.customerCompanyId}`) ? { ...shop, checked } : shop,
      ),
    );
  };

  const onAddShop = (data: StoreEntity[]) => {
    /* ModalSelectStore ส่งกลับเฉพาะร้านที่เพิ่งเลือกเพิ่ม จึงต้องนำมาต่อท้ายและกันซ้ำ */
    const newShops = data
      .filter(
        (item) =>
          !shops.some((shop) => `${shop.customerCompanyId}` === `${item.customerCompanyId}`),
      )
      .map((item) => ({ ...item, checked: false }));
    setShops([...shops, ...newShops]);
    setErrorMessage?.(undefined);
    setSelectShopModal(false);
  };

  const onDeleteShop = () => {
    setShops(shops.filter((shop) => !shop.checked));
    setDeleteShopModal(false);
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={isCheckedAll}
          indeterminate={!isCheckedAll && shopList.some((shop) => shop.checked)}
          disabled={shopList.length <= 0}
          onChange={(e) => onCheckAll(e.target.checked)}
        />
      ),
      dataIndex: "checked",
      key: "checked",
      width: "5%",
      render: (value: any, row: NewsShop) => (
        <Checkbox
          checked={!!value}
          onChange={(e) => onCheckShop(row.customerCompanyId, e.target.checked)}
        />
      ),
    },
    {
      title: "ชื่อร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      width: "35%",
      render: (value: any, row: NewsShop) => (
        <FlexCol>
          <Text fontFamily='Sarabun' level={5}>
            {value || "-"}
          </Text>
          {row.customerNo && (
            <Text fontFamily='Sarabun' level={6} color='Text3'>
              {row.customerNo}
            </Text>
          )}
        </FlexCol>
      ),
    },
    {
      title: "เขต",
      dataIndex: "zone",
      key: "zone",
      width: "30%",
      render: (value: any) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
    {
      title: "กลุ่มลูกค้า",
      dataIndex: "customerGroupName",
      key: "customerGroupName",
      width: "30%",
      render: (value: any) => (
        <Text fontFamily='Sarabun' level={5}>
          {value || "-"}
        </Text>
      ),
    },
  ];

  return (
    <>
      <Row align='middle' style={{ paddingBottom: 0 }}>
        <Text fontSize={16} fontWeight={700}>
          ร้านค้าที่เห็นข่าว
        </Text>
        <Text level={5} color='error'>
          *
        </Text>
      </Row>
      {errorMessage && (
        <Text color='error' fontSize={12} fontFamily='Sarabun'>
          {errorMessage}
        </Text>
      )}
      <Row
        justify='space-between'
        gutter={8}
        style={{ paddingTop: 8, alignItems: "baseline" }}
        align='middle'
      >
        <Col span={6}>
          <Input
            value={searchText}
            placeholder='ค้นหารหัส / ชื่อร้านค้า'
            suffix={<SearchOutlined style={{ color: color.Grey }} />}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Select
            value={searchZone || undefined}
            allowClear
            placeholder='เขตทั้งหมด'
            data={zoneList.map((z) => ({
              label: z.zoneName,
              value: z.zoneName,
              key: z.zoneId,
            }))}
            style={{ width: "100%", fontFamily: "Sarabun" }}
            onChange={(value) => setSearchZone(value || "")}
          />
        </Col>
        <Col span={4}>
          <Select
            value={searchGroup || undefined}
            style={{ width: "100%" }}
            placeholder='กลุ่มลูกค้าทั้งหมด'
            data={[
              ...groupList.map((group) => ({
                label: group.customerGroupName,
                key: group.customerGroupName,
                value: group.customerGroupName,
              })),
            ]}
            allowClear
            onChange={(value) => setSearchGroup(value || "")}
          />
        </Col>
        <Col flex='auto' />
        {checkedCount > 0 && (
          <Col>
            <DeleteOutlined
              onClick={() => setDeleteShopModal(true)}
              style={{ fontSize: 20, color: color.error, cursor: "pointer" }}
            />
          </Col>
        )}
        <Col span={4}>
          <Button
            typeButton='primary'
            title='เพิ่มร้านค้า'
            icon={<PlusOutlined style={{ color: color.white }} />}
            onClick={() => setSelectShopModal(true)}
          />
        </Col>
      </Row>
      <Row justify='end' style={{ padding: "8px 0 16px" }}>
        <Text level={6} color='Text3'>
          {`จำนวนที่เลือก ${shops.length} ร้าน`}
        </Text>
      </Row>
      <ShopTable
        bordered={false}
        loading={loading}
        columns={columns as any}
        dataSource={shopList as any}
        rowKey='customerCompanyId'
        pagination={false}
        scroll={{ y: 360 }}
        locale={{
          emptyText: (
            <NewsShopEmpty text={shops.length > 0 ? "ไม่พบร้านค้า" : "กรุณาเพิ่มร้านค้า"} />
          ),
        }}
      />
      {selectShopModal && (
        <ModalSelectStore
          company={company}
          showModalShop={selectShopModal}
          onClose={() => setSelectShopModal(false)}
          currentSelectShop={shops}
          callBackShop={onAddShop}
        />
      )}
      {deleteShopModal && (
        <Modal
          open={deleteShopModal}
          centered
          closable={false}
          maskClosable={false}
          width={440}
          okText='ยืนยัน'
          cancelText='ยกเลิก'
          onOk={onDeleteShop}
          onCancel={() => setDeleteShopModal(false)}
          okButtonProps={{
            style: { color: color.white, backgroundColor: color.error, border: "none" },
          }}
          cancelButtonProps={{
            style: { borderColor: color.error, color: color.error },
          }}
        >
          <Text level={3} fontWeight={700}>
            {`ลบร้านค้าที่เลือก ${checkedCount} ร้าน`}
          </Text>
          <div style={{ paddingTop: 8 }}>
            <Text level={5} color='Text3'>
              โปรดยืนยันการลบร้านค้า
            </Text>
          </div>
        </Modal>
      )}
    </>
  );
};

export default NewsStepTwoShop;
